import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, ScrollMaskedLine } from "@/components/Reveal";
import { PathwayHero, TaskLineVisual } from "@/components/PathwayVisuals";
import { BranchVisual } from "@/components/DisequilibriumVisual";
import { usePageTitle } from "@/lib/usePageTitle";

function Eyebrow({ children, testid }) {
  return (
    <Reveal>
      <p className="text-xs tracking-[0.25em] uppercase text-fog" data-testid={testid}>
        {children}
      </p>
    </Reveal>
  );
}

export default function ResearchNote02() {
  usePageTitle("Research Note 02: The Path to Expertise | Mentheris", "What happens when AI removes the path to becoming an expert? An emerging Mentheris research question.");
  return (
    <main className="pt-24 md:pt-32" data-testid="research-note-02">
      {/* ---------------- METADATA + HERO ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pb-16 md:pb-20">
        <MaskedLine delay={0.1}>
          <span className="text-xs tracking-[0.3em] uppercase text-fog" data-testid="note-label">
            Research Note · 02
          </span>
        </MaskedLine>
        <Reveal delay={0.25} className="mt-6">
          <div className="flex flex-wrap gap-x-10 gap-y-3 text-[11px] tracking-[0.2em] uppercase text-fog" data-testid="note-meta">
            <span>June 2026</span>
            <span>Emerging question</span>
            <span className="text-fog/60">A question worth following.</span>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-12 gap-12 items-center mt-12">
          <div className="md:col-span-6">
            <h1 className="font-serif font-medium tracking-tighter leading-[1.05] text-4xl md:text-6xl" data-testid="note-headline">
              <MaskedLine delay={0.35}>What happens when AI removes</MaskedLine>
              <MaskedLine delay={0.55}>
                <span className="italic text-ember">the path to becoming an expert?</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.85} className="mt-8 max-w-xl space-y-5">
              <p className="text-base md:text-lg text-ink/80 leading-relaxed">
                Automation may change more than the work we do. It may change how expertise is built.
              </p>
              <p className="text-base text-fog leading-relaxed">
                Most conversations about AI and work begin with a familiar question: which tasks
                can be automated? But another question sits underneath it: what were people
                learning while doing those tasks?
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <PathwayHero className="w-full hidden md:block" />
            <PathwayHero mobile className="w-full md:hidden" />
          </div>
        </div>
      </section>

      {/* ---------------- 01 THE QUESTION BEHIND THE QUESTION ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="beyond-task-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="beyond-task-eyebrow">01 · Looking beyond the task</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-3xl leading-[1.12]">
              <ScrollMaskedLine>Automation evaluates outputs.</ScrollMaskedLine>
              <ScrollMaskedLine delay={0.12}>Expertise develops through experience.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5">
              <p className="text-fog leading-relaxed">
                Expertise rarely appears fully formed. In many professions, it develops gradually:
                through routine work, repeated exposure, mistakes, judgement, increasingly
                difficult decisions and accumulated experience.
              </p>
              <p className="text-fog leading-relaxed">
                Junior work therefore has more than one function. It produces an immediate output,
                but it can also create the experience from which future capability develops.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-12 md:pl-10">
              <p className="font-serif italic text-2xl md:text-3xl leading-[1.3] tracking-tight text-ink/85" data-testid="task-pullquote">
                A task can produce both an output and a learner.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- 03 THE LINE TO REMEMBER ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32" data-testid="statement-section">
        <h2 className="font-serif font-medium tracking-tighter leading-[1.1] text-4xl md:text-6xl max-w-4xl">
          <ScrollMaskedLine>We automate tasks.</ScrollMaskedLine>
          <ScrollMaskedLine delay={0.15}>
            <span className="italic text-ember">But careers are built through sequences of tasks.</span>
          </ScrollMaskedLine>
        </h2>
        <TaskLineVisual className="mt-14 w-full max-w-2xl" />
      </section>

      {/* ---------------- 02 REFRAMING AUTOMATION ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="reframing-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <Eyebrow testid="reframing-eyebrow">02 · Reframing automation</Eyebrow>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight max-w-2xl">
              <ScrollMaskedLine>Perhaps the unit of analysis is too small.</ScrollMaskedLine>
            </h2>
            <Reveal delay={0.15} className="mt-8 max-w-2xl space-y-5">
              <p className="text-fog leading-relaxed">
                A task may look replaceable when considered in isolation. But careers and expertise
                develop across sequences of tasks, interactions and increasing responsibility.
                Evaluating automation one task at a time may therefore miss what that task
                contributes to a longer developmental pathway.
              </p>
              <p className="text-fog leading-relaxed">
                This does not mean routine work should be preserved simply because it has always
                existed. It means that before removing a task, we may need to understand what
                people were becoming capable of by doing it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- 03 QUESTIONS TO CARRY FORWARD ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28" data-testid="questions-section">
        <Eyebrow testid="questions-eyebrow">03 · Emerging questions</Eyebrow>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 mb-14 md:mb-20 max-w-3xl">
          <ScrollMaskedLine>What do we need to understand before the pathway changes?</ScrollMaskedLine>
        </h2>
        <div className="space-y-10 md:space-y-14 max-w-4xl">
          {[
            "Which tasks carry developmental value beyond their immediate output?",
            "What forms of experience remain necessary for developing judgement?",
            "How can new learning pathways reproduce capabilities that previously developed through junior work?",
            "Who becomes capable of challenging an AI system if fewer people develop deep expertise in the systems AI increasingly mediates?",
          ].map((q, i) => (
            <div key={i} className="grid md:grid-cols-12 gap-4 items-baseline" data-testid={`question-${i}`}>
              <span className="md:col-span-1 font-serif italic text-2xl text-ember">?</span>
              <h3 className="md:col-span-11 font-serif text-2xl md:text-3xl leading-[1.3] tracking-tight text-ink/85">
                <ScrollMaskedLine delay={i * 0.08}>{q}</ScrollMaskedLine>
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FINAL RESEARCH STATEMENT ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="final-statement-section">
        <Reveal className="max-w-2xl space-y-5">
          <p className="text-fog leading-relaxed">
            This note does not argue that expertise pipelines are already collapsing across
            professions. The observation is narrower: AI-driven automation may alter not only the
            distribution of work, but also the mechanisms through which expertise is reproduced.
          </p>
          <p className="text-fog leading-relaxed">That relationship deserves closer investigation.</p>
        </Reveal>
        <div className="mt-16 md:mt-20 grid md:grid-cols-12 gap-10 items-end">
          <h2 className="md:col-span-8 font-serif font-medium tracking-tighter leading-[1.12] text-3xl md:text-5xl max-w-3xl">
            <ScrollMaskedLine>The question is not only what AI can replace.</ScrollMaskedLine>
            <ScrollMaskedLine delay={0.15}>
              <span className="italic text-ember">It is what must still be learned.</span>
            </ScrollMaskedLine>
          </h2>
          <div className="md:col-span-4">
            <BranchVisual className="w-full max-w-[220px]" />
          </div>
        </div>
      </section>

      {/* ---------------- RESEARCH CONTEXT ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="context-section">
        <Reveal className="max-w-2xl">
          <p className="text-sm text-fog leading-relaxed">
            This Research Note develops an emerging question raised during the first Mentheris
            Research Dialogue in May 2026, where participants discussed how AI adoption may affect
            established expertise pathways in technically sophisticated fields.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
            <Link
              to="/session/session1"
              data-testid="context-dialogue-link"
              className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              Explore Research Dialogue 01
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/research"
              data-testid="context-research-link"
              className="group inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              Explore Mentheris Research
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ---------------- CONTINUE THE INQUIRY ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-24" data-testid="inquiry-section">
        <Eyebrow testid="inquiry-eyebrow">Continue the inquiry</Eyebrow>
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-6 max-w-3xl">
          <ScrollMaskedLine>Are you seeing this happen in your field?</ScrollMaskedLine>
        </h2>
        <Reveal delay={0.15} className="mt-6 max-w-2xl">
          <p className="text-fog leading-relaxed">
            If AI is changing junior work, training pathways or the way expertise develops in your
            profession or organisation, I would be interested in understanding what you are
            observing.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <a
            href="mailto:contact@mentheris.com"
            data-testid="inquiry-cta"
            className="group inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 text-sm tracking-wide hover:bg-ember transition-colors duration-300 mt-10"
          >
            Share a perspective
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>

      {/* ---------------- AUTHOR + RELATED ---------------- */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-12 pt-8 pb-24 md:pb-32" data-testid="note-footer">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-[10px] tracking-[0.3em] uppercase text-fog/70 mb-4">Author</p>
            <p className="font-serif text-xl tracking-tight">Dr. Elena Puică</p>
            <p className="mt-1 text-sm text-fog">Founder & Principal Researcher, Mentheris</p>
            <Link
              to="/about"
              data-testid="author-link"
              className="group inline-flex items-center gap-1.5 mt-4 text-xs tracking-[0.2em] uppercase text-fog hover:text-ink transition-colors duration-300"
            >
              About the author
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
          <div className="md:col-span-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-fog/70 mb-6">Continue exploring</p>
            {[
              {
                tag: "Research Note · 01",
                title: "The adaptive gap: what happens when capability begins to fall behind?",
                to: "/research-note-01",
                testid: "explore-note-01",
              },
              {
                tag: "Research Dialogue · 01",
                title: "The adaptation gap: when technology outpaces human capacity",
                to: "/session/session1",
                testid: "explore-dialogue",
              },
              {
                tag: "Research programme",
                title: "Technology-Human Disequilibrium",
                to: "/research",
                testid: "explore-thd",
              },
            ].map((r) => (
              <Reveal key={r.testid}>
                <Link
                  to={r.to}
                  data-testid={r.testid}
                  className="group relative flex items-baseline justify-between gap-6 py-5"
                >
                  <span className="absolute left-0 bottom-0 h-[2px] w-0 bg-leaf transition-all duration-500 ease-out group-hover:w-full" aria-hidden="true" />
                  <span>
                    <span className="block text-[10px] tracking-[0.25em] uppercase text-fog mb-1.5">{r.tag}</span>
                    <span className="font-serif text-lg md:text-xl leading-snug transition-transform duration-500 group-hover:translate-x-1 inline-block">
                      {r.title}
                    </span>
                  </span>
                  <ArrowRight size={15} className="shrink-0 text-fog transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
